import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { T } from '../../utils/theme';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Logo } from '../ui';
import FloatingTabBar from './FloatingTabBar';
import SheetMais from './SheetMais';
import { Home, Calendar, FileText, LogOut, Users, Scissors, CreditCard, DollarSign, BarChart3, List, Wallet, MessageCircle } from 'lucide-react';
import { listFiliais } from '../../services/multiLojasService';

const ADMIN_TABS = [
  { key: 'home', label: 'Inicio', path: '/' },
  { key: 'fila', label: 'Fila', path: '/fila' },
  { key: 'comandas', label: 'Comandas', path: '/comandas' },
  { key: 'gestao-saques', label: 'Saques', path: '/gestao-saques' },
  { key: 'mais', label: 'Mais', path: null },
];

const BARBER_TABS = [
  { key: 'home', label: 'Home', path: '/' },
  { key: 'fila', label: 'Fila', path: '/fila' },
  { key: 'comandas', label: 'Comanda', path: '/comandas' },
  { key: 'comissoes', label: 'Comissoes', path: '/comissoes' },
];

const SIDEBAR_ITEMS_GESTOR = [
  { icon: Home, label: 'Dashboard', path: '/' },
  { icon: FileText, label: 'Comandas', path: '/comandas' },
  { icon: Calendar, label: 'Agendamentos', path: '/agenda' },
  { icon: List, label: 'Fila', path: '/fila' },
  { icon: Scissors, label: 'Servicos', path: '/servicos' },
  { icon: Users, label: 'Barbeiros', path: '/barbeiros' },
  { icon: Users, label: 'Usuarios', path: '/usuarios' },
  { icon: BarChart3, label: 'Relatorios', path: '/relatorios' },
  { icon: CreditCard, label: 'Faturamento', path: '/faturamento' },
  { icon: Wallet, label: 'Gestao de Saques', path: '/gestao-saques' },
  { icon: DollarSign, label: 'Financeiro', path: '/financeiro' },
  { icon: MessageCircle, label: 'WhatsApp', path: '/whatsapp' },
];

const SIDEBAR_ITEMS_BARBEIRO = [
  { icon: Home, label: 'Inicio', path: '/' },
  { icon: List, label: 'Fila', path: '/fila' },
  { icon: FileText, label: 'Comandas', path: '/comandas' },
  { icon: Calendar, label: 'Agenda', path: '/agenda' },
  { icon: DollarSign, label: 'Comissoes', path: '/comissoes' },
];

const PATH_TO_KEY = {
  '/': 'home', '/fila': 'fila', '/comandas': 'comandas',
  '/gestao-saques': 'gestao-saques', '/comissoes': 'comissoes',
  '/agenda': 'agendamentos', '/servicos': 'servicos',
  '/barbeiros': 'barbeiros', '/usuarios': 'usuarios',
  '/relatorios': 'relatorios', '/faturamento': 'faturamento',
  '/financeiro': 'financeiro', '/disponibilidade': 'disponibilidade',
  '/whatsapp': 'whatsapp', '/nova-comanda': 'comandas',
};

const PAGE_NAMES = {
  home: 'Início', fila: 'Fila', comandas: 'Comandas',
  'gestao-saques': 'Saques', comissoes: 'Comissões',
  agendamentos: 'Agenda', servicos: 'Serviços',
  barbeiros: 'Barbeiros', usuarios: 'Usuários',
  relatorios: 'Relatórios', faturamento: 'Faturamento',
  financeiro: 'Financeiro', disponibilidade: 'Disponibilidade',
  whatsapp: 'WhatsApp',
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { carregarDados } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [maisOpen, setMaisOpen] = useState(false);
  const mainRef = useRef(null);
  const [scrolledDown, setScrolledDown] = useState(false);

  const activeKey = PATH_TO_KEY[location.pathname] || 'home';
  const pageName = PAGE_NAMES[activeKey] || 'Início';

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    setScrolledDown(false);
  }, [location.pathname]);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolledDown(el.scrollTop > 54);
          ticking = false;
        });
        ticking = true;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  const isGestor = user?.tipo === 'gestor' || user?.tipo === 'caixa';
  const isBarbeiro = user?.tipo === 'barbeiro';

  const tabs = isGestor ? ADMIN_TABS : BARBER_TABS;
  const sidebarItems = isGestor ? SIDEBAR_ITEMS_GESTOR
    : isBarbeiro ? SIDEBAR_ITEMS_BARBEIRO
    : SIDEBAR_ITEMS_GESTOR;

  if (user?.barbeariaSuspended) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>{children}</div>
      </div>
    );
  }

  const handleTabSelect = (key) => {
    if (key === 'mais') { setMaisOpen(true); return; }
    const tab = tabs.find(t => t.key === key);
    if (tab?.path) navigate(tab.path);
  };

  const handleNavigate = (view) => {
    const paths = {
      servicos: '/servicos', barbeiros: '/barbeiros', usuarios: '/usuarios',
      faturamento: '/faturamento', relatorios: '/relatorios', financeiro: '/financeiro',
    };
    navigate(paths[view] || '/');
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ background: T.bg }}>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:sticky md:top-0 md:h-screen"
        style={{ background: T.surface, borderRight: `1px solid ${T.separator}` }}>
        <div style={{ padding: 24, borderBottom: `1px solid ${T.separator}`, display: 'flex', justifyContent: 'center' }}>
          <Logo className="w-12 h-12" />
        </div>

        <div style={{ padding: 16, borderBottom: `1px solid ${T.separator}` }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: 12, borderRadius: 14, background: T.fill,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 20, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${T.accent}, ${T.indigo})`,
              color: '#fff', fontWeight: 600, fontSize: 16,
            }}>{user?.nome?.charAt(0)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontWeight: 600, fontSize: 14, color: T.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.nome}</p>
              <p style={{ fontSize: 12, color: T.label2, textTransform: 'capitalize' }}>{user?.tipo}</p>
            </div>
          </div>
        </div>

        <nav style={{ padding: 16, flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {sidebarItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', borderRadius: 14, border: 'none',
                  background: isActive ? `linear-gradient(135deg, ${T.accent}, ${T.indigo})` : 'transparent',
                  color: isActive ? '#fff' : T.label2,
                  fontFamily: 'inherit', fontSize: 14, fontWeight: isActive ? 600 : 500,
                  cursor: 'pointer', transition: 'all 200ms',
                  letterSpacing: '-0.01em',
                }}>
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div style={{ padding: 16, borderTop: `1px solid ${T.separator}` }}>
          <button onClick={logout} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', borderRadius: 14, border: 'none',
            background: T.fill, color: T.label, cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
          }}>
            <LogOut size={20} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Content area */}
      <div className="flex-1 flex flex-col min-w-0 w-full">
        {/* Scroll header pill — mobile only */}
        <div className="md:hidden" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 45,
          display: 'flex', justifyContent: 'center',
          paddingTop: 'max(10px, env(safe-area-inset-top, 10px))',
          transform: scrolledDown ? 'translateY(0)' : 'translateY(-120%)',
          opacity: scrolledDown ? 1 : 0,
          transition: 'transform 340ms cubic-bezier(0.32, 0.72, 0, 1), opacity 240ms ease',
          pointerEvents: 'none',
        }}>
          <div style={{
            background: 'rgba(0,0,0,0.72)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            borderRadius: 20,
            padding: '6px 16px',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}>
            {pageName}
          </div>
        </div>

        {/* Page content */}
        <main ref={mainRef} className="flex-1 overflow-auto" style={{ paddingBottom: 100 }}>
          {children}
        </main>
      </div>

      {/* Mobile floating tab bar */}
      <FloatingTabBar items={tabs} activeKey={maisOpen ? 'mais' : activeKey} onSelect={handleTabSelect} />

      {/* Sheet Mais (admin only) */}
      {isGestor && (
        <SheetMais open={maisOpen} onClose={() => setMaisOpen(false)}
          onNavigate={handleNavigate} onLogout={logout} />
      )}
    </div>
  );
};

export default Layout;
